<script setup lang="ts">
import UIToolTip from '@/components/ui/UIToolTip.vue'
import { useDetectBreakpoint } from '@/composables/useDetectBreakpoint'
import { TTooltipAlignment, TTooltipDirection } from '@lifk/types'
import { PhQuestion } from '@phosphor-icons/vue'
import { computed } from 'vue'

withDefaults(
  defineProps<{
    /** What text should be displayed in the button’s tooltip? (The tooptip will not be displayed here, only in the actual application.) */
    tooltipText: string
    /** In which direction should the tooltip appear with respect to the button? */
    tooltipDirection: TTooltipDirection
    /** How should the tooltip be aligned with respect to the button? */
    tooltipAlignment: TTooltipAlignment
    /** What text size should be used in the tooltip? */
    tooltipTextSize?: 'auto' | 'small' | 'default'
    /** What accessible label should be used, instead of the tooltip text? */
    label?: string
    /** What variant of the button should be used? */
    variant?: keyof typeof variantMapping
    /** Is the button disabled? */
    disabled?: boolean
    /** What HTML control type should be applied to the button? */
    buttonType?: 'button' | 'submit'
    /** 'Does the button have a popup (e.g. a menu)? (Used to set aria attributes) */
    hasPopup?: boolean
    /** If the button has a popup, is it currently expanded? (Used to set aria attributes) */
    expanded?: boolean
  }>(),
  {
    variant: 'default',
    buttonType: 'submit'
  }
)

const variantMapping = {
  default: '',
  mapControls: 'p-[7px]',
  extraPadding: 'p-2',
  uiButtonBar: 'px-[6px] py-[6px]',
  outlineSafariPatch: 'p-1 -outline-offset-4 focus:outline focus:outline-2' // use for elements that do not show focus ring in Safari
}

const isMobile = computed(() => useDetectBreakpoint('xs'))
</script>

<template>
  <UIToolTip
    :text="tooltipText"
    :direction="tooltipDirection"
    :alignment="tooltipAlignment"
    :text-size="tooltipTextSize"
    :disabled="disabled || isMobile"
    class="group rounded"
  >
    <div>
      <button
        class="block"
        :class="variantMapping[variant]"
        :disabled="disabled"
        :type="buttonType"
        :aria-label="label ?? tooltipText"
        :aria-haspopup="hasPopup"
        :aria-expanded="hasPopup ? expanded : undefined"
      >
        <slot>
          <PhQuestion size="20" class="fill-slate-tint-1" weight="regular" />
        </slot>
      </button>
    </div>
  </UIToolTip>
</template>
